/**
 * src/services/spatial/map/mapApiClient.ts
 *
 * Client for GeoLink's normalized `/api/map/*` API (Phase 2). Transport:
 *   - the browser calls the GeoTrust backend map proxy
 *     (`getMapApiBaseUrl()` → `/api/v2/geolink/map`), which injects the
 *     server-side `GEOLINK_MAP_API_KEY` and forwards to `GEOLINK_MAP_BASE_URL`.
 *     The browser NEVER holds the map key — request paths are relative
 *     (`/spatial-objects`, …), not the upstream `/api/map/...` paths;
 *   - reads are anonymous-friendly (public layers). An `apiKey` option is
 *     supported only for SSR/Node/tests that bypass the proxy;
 *   - errors carry `.status` so callers can hide a layer on 401/403 and
 *     back off on 429 (Phase 1 permission-denied mechanism, generalized).
 *
 * GeoLink stays authoritative for authorization/filtering/privacy — this
 * client only fetches and never decides what a user may see.
 */

import { getMapApiBaseUrl } from '../spatialEnv';
import type {
  MapDiscovery,
  MapFeatureCollection,
  MapPathParams,
  MapReadParams,
  MapSummaryProperties,
  CountryPolicyProperties,
} from './mapTypes';

/** Hard client-side ceiling matching the API's documented `limit ≤ 1000`. */
export const MAP_LIMIT_MAX = 1000;

function toBbox(bbox: MapReadParams['bbox']): string | undefined {
  if (bbox == null) return undefined;
  if (typeof bbox === 'string') return bbox;
  if (Array.isArray(bbox) && bbox.length === 4) {
    return bbox.map(n => Number(n).toFixed(6)).join(',');
  }
  return undefined;
}

function buildQuery(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') continue;
    if (k === 'bbox') {
      const b = toBbox(v as MapReadParams['bbox']);
      if (b) q.set('bbox', b);
      continue;
    }
    if (k === 'limit') {
      const n = Math.min(Number(v), MAP_LIMIT_MAX);
      if (Number.isFinite(n) && n > 0) q.set('limit', String(Math.floor(n)));
      continue;
    }
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export class MapApiClient {
  private baseUrl: string;
  private apiKey: string | null;

  constructor(opts: { baseUrl?: string; apiKey?: string | null } = {}) {
    this.baseUrl = (opts.baseUrl ?? getMapApiBaseUrl()).replace(/\/+$/, '');
    // Default null: the browser routes through the proxy, which owns the key.
    // Only set for SSR/Node/tests that talk to GeoLink (or a mock) directly.
    this.apiKey = opts.apiKey ?? null;
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(init.headers as Record<string, string> | undefined),
    };
    // Public layers work anonymously; only direct (non-proxy) callers set a key.
    if (this.apiKey && this.apiKey.trim()) headers['X-API-Key'] = this.apiKey.trim();

    const url = `${this.baseUrl}${path.startsWith('/') ? path : '/' + path}`;
    const res = await fetch(url, { ...init, headers });
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        msg = body?.reason || body?.error || body?.message || msg;
      } catch { /* ignore */ }
      const err = new Error(`[mapApi] ${path}: ${msg}`);
      (err as { status?: number }).status = res.status;
      throw err;
    }
    const text = await res.text();
    if (!text) return undefined as unknown as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new Error(`[mapApi] ${path}: invalid JSON response`);
    }
  }

  /** `GET /api/map/` — endpoint list + resolved auth context for this key. */
  discover(): Promise<MapDiscovery> {
    return this.request<MapDiscovery>('/');
  }

  // ─── List reads (always send bbox + zoom) ─────────────────────────
  // Paths are relative to the map proxy base (which already targets
  // `<host>/api/map`), so we omit the `/api/map` prefix here.

  getSpatialObjects(params: MapReadParams = {}): Promise<MapFeatureCollection> {
    return this.request(`/spatial-objects${buildQuery(params)}`);
  }

  getSpatialObject(id: string): Promise<import('./mapTypes').MapFeature | null> {
    return this.request<import('./mapTypes').MapFeature>(
      `/spatial-objects/${encodeURIComponent(id)}`,
    ).catch((e) => {
      if ((e as { status?: number }).status === 404) return null;
      throw e;
    });
  }

  getCountryPolicies(params: MapReadParams = {}): Promise<MapFeatureCollection<CountryPolicyProperties>> {
    return this.request(`/country-policies${buildQuery(params)}`);
  }

  getExecutionRules(params: MapReadParams = {}): Promise<MapFeatureCollection> {
    return this.request(`/execution-rules${buildQuery(params)}`);
  }

  getRuleEvents(params: MapReadParams = {}): Promise<MapFeatureCollection> {
    return this.request(`/rule-events${buildQuery(params)}`);
  }

  getAgents(params: MapReadParams = {}): Promise<MapFeatureCollection> {
    return this.request(`/agents${buildQuery(params)}`);
  }

  getAgentPath(id: string, params: MapPathParams = {}): Promise<MapFeatureCollection> {
    return this.request(`/agents/${encodeURIComponent(id)}/path${buildQuery(params as Record<string, unknown>)}`);
  }

  /** Auth required (401/403 when anonymous). */
  getLiveSessions(params: MapReadParams = {}): Promise<MapFeatureCollection> {
    return this.request(`/live-sessions${buildQuery(params)}`);
  }

  getPresenceCells(params: MapReadParams = {}): Promise<MapFeatureCollection> {
    return this.request(`/presence-cells${buildQuery(params)}`);
  }

  getDrones(params: MapReadParams = {}): Promise<MapFeatureCollection> {
    return this.request(`/drones${buildQuery(params)}`);
  }

  getDronePath(id: string, params: MapPathParams = {}): Promise<MapFeatureCollection> {
    return this.request(`/drones/${encodeURIComponent(id)}/path${buildQuery(params as Record<string, unknown>)}`);
  }

  /**
   * Fetch every page of a list endpoint, following `pagination.nextCursor`.
   * Bounded by `maxPages` so a runaway cursor can't loop forever.
   */
  async fetchAllPages<P extends MapSummaryProperties = MapSummaryProperties>(
    fetchPage: (params: MapReadParams) => Promise<MapFeatureCollection<P>>,
    params: MapReadParams = {},
    maxPages = 20,
  ): Promise<MapFeatureCollection<P>> {
    const all: MapFeatureCollection<P>['features'] = [];
    let cursor: string | undefined = params.cursor as string | undefined;
    let pages = 0;
    let meta: MapFeatureCollection<P>['meta'];
    do {
      const page = await fetchPage({ ...params, cursor });
      if (Array.isArray(page.features)) all.push(...page.features);
      meta = page.meta;
      cursor = page.pagination?.hasMore ? (page.pagination?.nextCursor ?? undefined) : undefined;
      pages += 1;
    } while (cursor && pages < maxPages);
    return { type: 'FeatureCollection', features: all, meta, pagination: { hasMore: false, nextCursor: null } };
  }
}

/** Singleton used across the map subsystem. */
export const mapApiClient = new MapApiClient();
